import { App, View } from "@pimo/pimo-app-builder";
import { DragAndDropLayout, PMSmallTable } from "@pimo/pimo-components";
import { getLocalizedMessage } from "@pimo/pimo-l10n";

import { TestAppState } from "../../../app";
import { theme } from "../../../theme";
import { formatDate } from "../../../utils/format-date";
import { getProjectName } from "../../../utils/get-plural-of-project-name";

/** Adds the project duration card (#2222). */
export function addProjectDurationCard(
  view: View<TestAppState, DragAndDropLayout>,
  app: App<TestAppState>
) {
  const state = app.getAppState();
  const language = state.language || "en";
  const { settings } = state;
  const projectName = getProjectName(
    settings?.configuration?.projectName,
    language
  );

  const projectDurationComponent = view.addComponent({
    component: PMSmallTable,
    layoutProps: {
      componentId: "projectDurationComponent",
      labels: ["duration"],
      description:
        getLocalizedMessage(language, "showsProjectDuration", {
          project: projectName,
        }) || "Shows the project duration.",
      title:
        getLocalizedMessage(language, "projectDuration", {
          project: projectName,
        }) || "Project Duration",
    },
  });

  projectDurationComponent.mapState(({ settings, currentReport, language }) => {
    const localizedProjectName =
      getProjectName(settings?.configuration?.projectName, language) ||
      getLocalizedMessage(language, "project") ||
      "Project";
    const items: {
      label: string;
      value: string;
      chipColor: string;
      chipTextColor: string;
    }[] = [];

    for (const field of settings?.configuration?.fieldsDefinition?.filter(
      (field) => field.purpose === "itemBasic"
    ) ?? []) {
      if (field.fieldName === "startDate" && field.enabled) {
        items.push({
          label: "Start",
          value: formatDate(currentReport?.project?.startDate),
          chipColor: theme.palette.secondary.main,
          chipTextColor: theme.palette.primary.main,
        });
      } else if (field.fieldName === "endDate" && field.enabled) {
        items.push({
          label: "End",
          value: formatDate(currentReport?.project?.endDate),
          chipColor: theme.palette.secondary.main,
          chipTextColor: theme.palette.primary.main,
        });
      }
    }

    return {
      showInfoIcon: false,
      showMenuIcon: false,
      title:
        getLocalizedMessage(language, "projectDuration", {
          project: localizedProjectName,
        }) ||
        `${localizedProjectName} ${getLocalizedMessage(language, "duration") ?? "Duration"}`,

      items,
    };
  });

  projectDurationComponent.mapVisibility(({ settings }) =>
    (
      settings?.configuration?.fieldsDefinition?.filter(
        (field) => field.purpose === "itemBasic"
      ) ?? []
    ).some(
      (field) =>
        field.enabled === true &&
        (field.fieldName === "startDate" || field.fieldName === "endDate")
    )
  );
}
